/**
 * WhatsApp Templates Manager
 * Gestione template messaggi per appuntamenti e comunicazioni
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { extractErrorMessage } from '../utils/errorUtils';
import { notify, confirmAsync } from '../utils/notify';
import './Templates.css';

// Sub-components
import TemplateList from './templates/TemplateList';
import TemplateForm from './templates/TemplateForm';
import TemplatePreview from './templates/TemplatePreview';
import ScheduledTemplates from './templates/ScheduledTemplates';
import SendTemplateModal from './templates/SendTemplateModal';
import TemplateHistory from './templates/TemplateHistory';

const Templates = () => {
  const { t } = useTranslation();
  
  // State
  const [activeTab, setActiveTab] = useState('list');
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      
      const response = await api.get(`/api/v1/templates?${params.toString()}`);
      setTemplates(response.data.templates);
      setStats({
        total: response.data.total,
        pending: response.data.pending,
        approved: response.data.approved,
        rejected: response.data.rejected
      });
    } catch (err) {
      setError(extractErrorMessage(err.response?.data?.detail, t('templates.errors.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, t]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Sync with Meta
  const handleSync = async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/v1/templates/sync');
      notify.success(t('templates.syncSuccess', {
        created: response.data.created,
        updated: response.data.updated
      }));
      fetchTemplates();
    } catch (err) {
      setError(extractErrorMessage(err.response?.data?.detail, t('templates.errors.syncFailed')));
    } finally {
      setLoading(false);
    }
  };

  // Delete template
  const handleDelete = async (templateId) => {
    if (!(await confirmAsync({ message: t('templates.confirmDelete'), danger: true }))) return;
    
    try {
      await api.delete(`/api/v1/templates/${templateId}`);
      fetchTemplates();
    } catch (err) {
      setError(extractErrorMessage(err.response?.data?.detail, t('templates.errors.deleteFailed')));
    }
  };

  // Open send modal
  const handleSendClick = (template) => {
    setSelectedTemplate(template);
    setShowSendModal(true);
  };

  // Open preview modal
  const handlePreviewClick = (template) => {
    setSelectedTemplate(template);
    setShowPreviewModal(true);
  };

  return (
    <div className="templates-container">
      {/* Header */}
      <div className="templates-header">
        <h1>{t('templates.title')}</h1>
        <p className="templates-subtitle">{t('templates.subtitle')}</p>
      </div>

      {/* Stats Cards */}
      <div className="templates-stats">
        <div className="stat-card total">
          <span className="stat-number">{stats.total}</span>
          <span className="stat-label">{t('templates.stats.total')}</span>
        </div>
        <div className="stat-card approved">
          <span className="stat-number">{stats.approved}</span>
          <span className="stat-label">{t('templates.stats.approved')}</span>
        </div>
        <div className="stat-card pending">
          <span className="stat-number">{stats.pending}</span>
          <span className="stat-label">{t('templates.stats.pending')}</span>
        </div>
        <div className="stat-card rejected">
          <span className="stat-number">{stats.rejected}</span>
          <span className="stat-label">{t('templates.stats.rejected')}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="templates-tabs">
        <button 
          className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          📋 {t('templates.tabs.myTemplates')}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'scheduled' ? 'active' : ''}`}
          onClick={() => setActiveTab('scheduled')}
        >
          ⏰ {t('templates.tabs.scheduled')}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          📊 {t('templates.tabs.history')}
        </button>
      </div>

      {/* Actions Bar */}
      {activeTab === 'list' && (
        <div className="templates-actions">
          <div className="filters">
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">{t('templates.filters.allStatus')}</option>
              <option value="APPROVED">{t('templates.status.approved')}</option>
              <option value="PENDING">{t('templates.status.pending')}</option>
              <option value="REJECTED">{t('templates.status.rejected')}</option>
            </select>
            
            <select 
              value={categoryFilter} 
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">{t('templates.filters.allCategories')}</option>
              <option value="UTILITY">{t('templates.category.utility')}</option>
              <option value="MARKETING">{t('templates.category.marketing')}</option>
              <option value="AUTHENTICATION">{t('templates.category.authentication')}</option>
            </select>
          </div>
          
          <div className="action-buttons">
            <button 
              className="btn-sync"
              onClick={handleSync}
              disabled={loading}
            >
              🔄 {t('templates.actions.sync')}
            </button>
            <button 
              className="btn-create"
              onClick={() => setShowCreateModal(true)}
            >
              ➕ {t('templates.actions.create')}
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Tab Content */}
      <div className="templates-content">
        {activeTab === 'list' && (
          <TemplateList
            templates={templates}
            loading={loading}
            onSend={handleSendClick}
            onPreview={handlePreviewClick}
            onDelete={handleDelete}
            onRefresh={fetchTemplates}
          />
        )}
        
        {activeTab === 'scheduled' && (
          <ScheduledTemplates />
        )}
        
        {activeTab === 'history' && (
          <TemplateHistory />
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <TemplateForm
          template={selectedTemplate}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedTemplate(null);
          }}
          onSaved={() => {
            setShowCreateModal(false);
            setSelectedTemplate(null);
            fetchTemplates();
          }}
        />
      )}

      {/* Send Template Modal */}
      {showSendModal && selectedTemplate && (
        <SendTemplateModal
          template={selectedTemplate}
          onClose={() => {
            setShowSendModal(false);
            setSelectedTemplate(null);
          }}
          onSent={() => {
            setShowSendModal(false);
            setSelectedTemplate(null);
          }}
        />
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedTemplate && (
        <TemplatePreview
          template={selectedTemplate}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedTemplate(null);
          }}
        />
      )}
    </div>
  );
};

export default Templates;
